// Role-center navigation. Each major module (Finance, HR, Payroll, Academics)
// gets its own collapsible side menu, resolved from the current path. Finance
// keeps its existing (broad) prefix matching from financeNav.js; the others
// match their own base path.
import {
  AccountBalance, People, Groups, School, Calculate,
  LocalShipping, Settings, DirectionsBus, LocalLibrary,
} from '@mui/icons-material';
import { FINANCE_NAV, isFinancePath } from './financeNav';

const FLEET_NAV = [
  { title: 'Overview', color: '#0277bd', items: [
    { label: 'Fleet Dashboard', path: '/fleet', exact: true },
  ] },
  { title: 'Operations', color: '#01579b', items: [
    { label: 'Learner Routes',        path: '/fleet/learners' },
    { label: 'Grouping & Allocation', path: '/fleet/grouping' },
    { label: 'Bus Manifests',         path: '/fleet/manifest' },
  ] },
  { title: 'Setup', color: '#455a64', items: [
    { label: 'Buses',           path: '/fleet/buses' },
    { label: 'Bus Stages',      path: '/fleet/stages' },
    { label: 'Routes',          path: '/fleet/routes' },
    { label: 'Time Setup',      path: '/fleet/time-setup' },
    { label: 'Transport Setup', path: '/fleet/setup' },
  ] },
];

const LIBRARY_NAV = [
  { title: 'Overview', color: '#5e35b1', items: [
    { label: 'Library Dashboard', path: '/library', exact: true },
  ] },
  { title: 'Circulation', color: '#4527a0', items: [
    { label: 'Issue / Return',       path: '/library/loans' },
    { label: 'Bulk Textbook Assign', path: '/library/bulk-assign' },
    { label: 'Overdue Books',        path: '/library/overdue' },
  ] },
  { title: 'Catalog', color: '#455a64', items: [
    { label: 'Books & Copies', path: '/library/catalog' },
    { label: 'Storage Areas',  path: '/library/storage-areas' },
  ] },
];

const HR_NAV = [
  { title: 'Employees', color: '#6a1b9a', items: [
    { label: 'Employees',   path: '/hr/employees' },
    { label: 'Departments', path: '/hr/departments' },
    { label: 'Pay Grades',  path: '/hr/pay-grades' },
    { label: 'Pay Groups',  path: '/hr/pay-groups' },
  ] },
  { title: 'Leave', color: '#00838f', items: [
    { label: 'Leave Dashboard', path: '/hr/leave' },
    { label: 'Applications',    path: '/hr/leave/applications' },
    { label: 'Leave Types',     path: '/hr/leave/types' },
    { label: 'Leave Groups',    path: '/hr/leave/groups' },
    { label: 'Leave Ledger',    path: '/hr/leave/ledger' },
    { label: 'Leave Statement', path: '/hr/leave/statement' },
    { label: 'Leave Report',    path: '/hr/leave/report' },
  ] },
  { title: 'Performance', color: '#1565c0', items: [
    { label: 'Appraisals',        path: '/hr/appraisal' },
    { label: 'Appraisal Setup',   path: '/hr/appraisal/setup' },
    { label: 'Appraisal Reports', path: '/hr/appraisal/reports' },
  ] },
  { title: 'Disciplinary & Exit', color: '#c62828', items: [
    { label: 'Disciplinary',         path: '/hr/disciplinary' },
    { label: 'Disciplinary Reports', path: '/hr/disciplinary/reports' },
    { label: 'Exit / Clearance',     path: '/hr/exit' },
    { label: 'Exit Reports',         path: '/hr/exit/reports' },
  ] },
];

const PAYROLL_NAV = [
  { title: 'Processing', color: '#c62828', items: [
    { label: 'Payroll Dashboard', path: '/payroll' },
    { label: 'Payroll Periods',   path: '/payroll/periods' },
    { label: 'Payroll Runs',      path: '/payroll/runs' },
    { label: 'Employees',         path: '/payroll/employees' },
  ] },
  { title: 'Reports', color: '#1565c0', items: [
    { label: 'Payroll Reports', path: '/payroll/reports' },
  ] },
  { title: 'Setup', color: '#455a64', items: [
    { label: 'Payroll Setup',     path: '/payroll/setup' },
    { label: 'Transaction Codes', path: '/payroll/setup/transactions' },
  ] },
];

const ACADEMICS_NAV = [
  { title: 'Overview', color: '#1b5e20', items: [
    // Exact match: /academics is a prefix of every other academics path, so
    // without `exact` this item would highlight on all sub-pages.
    { label: 'Academics Dashboard', path: '/academics', exact: true },
    // Learners lives at /learners (not under /academics) but belongs to this
    // module — moduleForPath() maps it to academics and the module match below
    // includes it so this menu stays visible there.
    { label: 'Learners', path: '/learners' },
    // Also surfaced under Assessment below; kept here for quick access.
    { label: 'Periodic Activities', path: '/academics/periodic-activities' },
  ] },
  { title: 'Structure', color: '#2e7d32', items: [
    { label: 'Academic Years', path: '/academics/years' },
    { label: 'Stages',         path: '/academics/stages' },
    { label: 'Streams',        path: '/academics/streams' },
    { label: 'Classes',        path: '/academics/classes' },
    { label: 'Learning Areas', path: '/academics/learning-areas' },
    { label: 'Stage Subjects', path: '/academics/stage-subjects' },
    { label: 'Subject Teachers', path: '/academics/subject-teachers' },
  ] },
  { title: 'Timetabling', color: '#00695c', items: [
    { label: 'Timetable Setup',     path: '/academics/timetable-setup' },
    { label: 'Teacher Allocations', path: '/academics/teacher-allocations' },
    { label: 'Timetable Grid',      path: '/academics/timetable-grid' },
    { label: 'Timetable Views',     path: '/academics/timetable-print' },
  ] },
  { title: 'Assessment', color: '#1565c0', items: [
    { label: 'Assessment Setup',    path: '/academics/assessment-setup' },
    { label: 'Score Entry',         path: '/academics/score-entry' },
    { label: 'Assessment Report',   path: '/academics/assessment-report' },
    { label: 'Merit List',          path: '/academics/merit-list' },
    { label: 'Report Forms',        path: '/academics/report-forms' },
    { label: 'LearnAlytics',        path: '/academics/lernalytics' },
    { label: 'Grading Systems',     path: '/setups/grading-systems' },
    { label: 'Periodic Activities', path: '/academics/periodic-activities' },
  ] },
  { title: 'Fees & Charges', color: '#e65100', items: [
    { label: 'Fee Templates', path: '/academics/fee-templates' },
    { label: 'Charge Items',  path: '/academics/charge-items' },
  ] },
  { title: 'Events', color: '#6a1b9a', items: [
    { label: 'School Events', path: '/academics/events' },
  ] },
  { title: 'Class Teacher', color: '#00838f', items: [
    { label: 'Class Teacher Centre', path: '/class-teacher-centre' },
  ] },
];

// Supply-chain groups (procurement + stores). These paths live under the
// payables/procurement modules but are surfaced here as their own role center.
const SUPPLY_NAV = [
  { title: 'Purchasing', color: '#e65100', items: [
    { label: 'Suppliers',         path: '/suppliers' },
    { label: 'Purchase Orders',   path: '/payables/orders' },
    { label: 'Purchase Invoices', path: '/payables/invoices' },
    { label: 'Purchase Receipts', path: '/payables/receipts' },
    { label: 'Payment Vouchers',  path: '/payables/vouchers' },
  ] },
  { title: 'Inventory', color: '#2e7d32', items: [
    { label: 'Items & Services',    path: '/payables/items' },
    { label: 'Item Journals',       path: '/payables/item-journals' },
    { label: 'Inventory Valuation', path: '/payables/inventory/valuation' },
    { label: 'Inventory Aging',     path: '/payables/inventory/aging' },
    { label: 'Reorder Report',      path: '/payables/inventory/reorder' },
  ] },
  { title: 'Stores', color: '#00695c', items: [
    { label: 'Store Requisitions', path: '/payables/requisitions' },
    { label: 'Transfer Orders',    path: '/procurement/transfers' },
    { label: 'Locations',          path: '/setups/locations' },
  ] },
];

// Setups / system configuration groups.
const SETUPS_NAV = [
  { title: 'Company & System', color: '#455a64', items: [
    { label: 'Company Details', path: '/setups/company' },
    { label: 'Currencies',      path: '/setups/currencies' },
    { label: 'Number Series',   path: '/setups/number-series' },
    { label: 'Locations',       path: '/setups/locations' },
  ] },
  { title: 'Posting', color: '#6a1b9a', items: [
    { label: 'Posting Setup',         path: '/setups/posting' },
    { label: 'Posting Groups',        path: '/setups/posting-groups' },
    { label: 'VAT Posting Groups',    path: '/setups/vat-posting-groups' },
    { label: 'Learner Posting Groups',path: '/setups/learner-posting-groups' },
    { label: 'Dimensions',            path: '/setups/dimensions' },
  ] },
  { title: 'Users & Security', color: '#1565c0', items: [
    { label: 'Users',           path: '/setups/users' },
    { label: 'User Groups',     path: '/setups/user-groups' },
    { label: 'Permission Sets', path: '/setups/permission-sets' },
    { label: 'Approvals',       path: '/setups/approvals' },
  ] },
  { title: 'Data', color: '#00838f', items: [
    { label: 'Data Import',   path: '/setups/data-import' },
    // Grading Scale lives under Academics → Assessment (it's an academic setup),
    // so it isn't duplicated here.
  ] },
];

// Top module bar (BC-style) — the main modules shown above Activities on the
// dashboard, each opening a mega-menu of its groups. `permission` is the key
// passed to canView() so a module only shows when the user can reach it.
export const TOP_MODULES = [
  { key: 'finance',   title: 'Finance',         Icon: AccountBalance, color: '#0a1931', home: '/finance',      permission: 'finance',      groups: FINANCE_NAV   },
  { key: 'academics', title: 'Academics',       Icon: School,         color: '#1b5e20', home: '/academics',    permission: 'academics',    groups: ACADEMICS_NAV },
  { key: 'supply',    title: 'Supply Chain',    Icon: LocalShipping,  color: '#e65100', home: '/supply-chain', permission: 'supply-chain', groups: SUPPLY_NAV    },
  { key: 'hr',        title: 'Human Resources', Icon: Groups,         color: '#4a148c', home: '/hr',           permission: 'hr',           groups: HR_NAV        },
  { key: 'payroll',   title: 'Payroll',         Icon: Calculate,      color: '#b71c1c', home: '/payroll',      permission: 'payroll',      groups: PAYROLL_NAV   },
  { key: 'fleet',     title: 'Fleet',           Icon: DirectionsBus,  color: '#0277bd', home: '/fleet',        permission: 'fleet',        groups: FLEET_NAV     },
  { key: 'library',   title: 'Library',         Icon: LocalLibrary,   color: '#5e35b1', home: '/library',      permission: 'library',      groups: LIBRARY_NAV   },
  { key: 'setups',    title: 'Setups',          Icon: Settings,       color: '#455a64', home: '/setups',       permission: 'setups',       groups: SETUPS_NAV    },
];

// Class-teacher side menu — only the pages a class teacher can actually reach.
// Mirrors the read-only-plus-assessment access enforced in App.js PrivateRoute
// and the backend scoping.
const CLASS_TEACHER_NAV = [
  { title: 'My Class', color: '#00838f', items: [
    { label: 'Class Teacher Centre', path: '/class-teacher-centre' },
  ] },
  { title: 'Assessment', color: '#1565c0', items: [
    { label: 'Assessment Setup',  path: '/academics/assessment-setup' },
    { label: 'Score Entry',       path: '/academics/score-entry' },
    { label: 'Assessment Report', path: '/academics/assessment-report' },
    { label: 'Merit List',        path: '/academics/merit-list' },
    { label: 'Report Forms',      path: '/academics/report-forms' },
    { label: 'LearnAlytics',      path: '/academics/lernalytics' },
  ] },
  { title: 'Events', color: '#6a1b9a', items: [
    { label: 'School Events', path: '/academics/events' },
  ] },
];

// Module descriptors. `match` decides whether a path belongs to the module.
const MODULES = [
  { key: 'finance',   title: 'Finance',         color: '#0a1931', Icon: AccountBalance, home: '/finance', groups: FINANCE_NAV,   match: isFinancePath },
  { key: 'hr',        title: 'Human Resources', color: '#4a148c', Icon: People,         home: '/hr',       groups: HR_NAV,        match: (p) => underBase(p, '/hr') },
  { key: 'payroll',   title: 'Payroll',         color: '#b71c1c', Icon: Calculate,      home: '/payroll',  groups: PAYROLL_NAV,   match: (p) => underBase(p, '/payroll') },
  { key: 'academics', title: 'Academics',       color: '#1b5e20', Icon: School,         home: '/academics',groups: ACADEMICS_NAV, match: (p) => underBase(p, '/academics') || underBase(p, '/learners') || p === '/class-teacher-centre' },
  { key: 'fleet',     title: 'Fleet',           color: '#0277bd', Icon: DirectionsBus,  home: '/fleet',    groups: FLEET_NAV,     match: (p) => underBase(p, '/fleet') },
  { key: 'library',   title: 'Library',         color: '#5e35b1', Icon: LocalLibrary,   home: '/library',  groups: LIBRARY_NAV,   match: (p) => underBase(p, '/library') },
];

function underBase(pathname, base) {
  return pathname === base || pathname.startsWith(base + '/');
}

// Pages that physically live under /setups but belong to ACADEMICS — they must
// show the Academics side menu (not Finance), so academic staff never land in a
// finance role center. Mirrors moduleForPath()'s 'academics' mapping for these.
const ACADEMIC_SETUP_PATHS = ['/setups/grading-scale', '/setups/grading-systems'];

// Resolve which module's side menu (if any) to show for the current path.
// A class teacher gets a scoped menu instead of the full Academics menu.
export function resolveModuleNav(pathname, role) {
  if (!pathname) return null;
  if (role === 'classTeacher') {
    const inScope = underBase(pathname, '/academics') || pathname === '/class-teacher-centre';
    if (!inScope) return null;
    return {
      key: 'classTeacher', title: 'Class Teacher', color: '#00838f',
      Icon: School, home: '/class-teacher-centre', groups: CLASS_TEACHER_NAV,
    };
  }
  // Academic setups override the finance-prefix match on /setups/*.
  if (ACADEMIC_SETUP_PATHS.some(p => underBase(pathname, p))) {
    return MODULES.find(m => m.key === 'academics') || null;
  }
  return MODULES.find(m => m.match(pathname)) || null;
}
